import { Link } from 'react-router-dom';
import { ArrowRight, FileText, BarChart3, Award, CheckCircle, Camera, MessageSquare, Users, TrendingUp } from 'lucide-react';
import { Button } from '../components/ui/button';

const reportIncludes = [
  { icon: FileText, text: 'Event overview and objectives' },
  { icon: Users, text: 'Audience snapshot and demographics' },
  { icon: TrendingUp, text: 'Engagement highlights and metrics' },
  { icon: MessageSquare, text: 'Feedback quotes from participants' },
  { icon: BarChart3, text: 'Outcomes and impact measurement' },
  { icon: Camera, text: 'Photo highlights from the event' },
];

const reportTypes = [
  {
    title: 'Snapshot Report',
    description: 'A concise overview of your event or session, perfect for quick impact summaries and internal updates.',
    features: [
      'Event summary (1-2 pages)',
      'Key attendance figures',
      'Top 3 feedback highlights',
      '5-10 photo highlights',
      'Quick turnaround (3-5 days)',
    ],
    color: '#E3B505',
    ideal: 'Single events, internal updates',
  },
  {
    title: 'Standard Insight Report',
    description: 'A comprehensive report with detailed analysis, participant feedback, and recommendations for future programming.',
    features: [
      'Full event analysis (5-8 pages)',
      'Detailed audience breakdown',
      'Complete feedback analysis',
      'Photo gallery (15-20 images)',
      'Recommendations for improvement',
      'Turnaround: 7-10 days',
    ],
    color: '#E76F51',
    ideal: 'Programme evaluation, stakeholder reporting',
  },
  {
    title: 'Partnership Impact Report',
    description: 'A premium report designed for partners and sponsors, showcasing ROI, brand visibility, and community impact.',
    features: [
      'Executive summary',
      'Full impact analysis (10+ pages)',
      'Brand visibility metrics',
      'Social media reach data',
      'Professional photo package',
      'Community testimonials',
      'Future partnership recommendations',
      'Turnaround: 10-14 days',
    ],
    color: '#264653',
    ideal: 'Sponsors, CSR reporting, board presentations',
  },
];

export const ReportsPage = () => {
  return (
    <main data-testid="reports-page" className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-28 bg-[#FAF9F6]">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="max-w-3xl">
            <span className="text-sm font-medium tracking-wide uppercase text-[#E3B505] mb-4 block">
              Impact & Insight Reports
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.1] text-[#2D2A26] mb-6 font-['Manrope']">
              Measure What Matters
            </h1>
            <p className="text-lg md:text-xl text-[#5C5852] leading-relaxed">
              OVYC offers comprehensive impact and insight reporting for selected events, sessions, 
              and community projects. Demonstrate the real value of your community investment with 
              data-driven insights.
            </p>
          </div>
        </div>
      </section>

      {/* What Reports Include */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <span className="text-sm font-medium tracking-wide uppercase text-[#E76F51] mb-4 block">
                Report Contents
              </span>
              <h2 className="text-3xl md:text-4xl font-semibold text-[#2D2A26] mb-6 font-['Manrope']">
                What Our Reports Include
              </h2>
              <p className="text-[#5C5852] text-lg leading-relaxed mb-8">
                Every OVYC report is designed to tell the story of your community impact. We combine 
                quantitative data with qualitative insights to give you a complete picture of outcomes 
                and value.
              </p>
              <div className="grid sm:grid-cols-2 gap-4">
                {reportIncludes.map((item, index) => (
                  <div 
                    key={index}
                    className="flex items-center gap-3 p-3 rounded-xl hover:bg-[#F4F1DE] transition-colors"
                  >
                    <div className="w-10 h-10 rounded-lg bg-[#E3B505]/10 flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-5 h-5 text-[#E3B505]" />
                    </div>
                    <span className="text-[#2D2A26] text-sm font-medium">{item.text}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="bg-[#F4F1DE] rounded-3xl p-8 md:p-10">
              <h3 className="text-xl font-semibold text-[#2D2A26] mb-6 font-['Manrope']">
                Why Impact Reporting?
              </h3>
              <div className="space-y-4">
                <p className="text-[#5C5852] leading-relaxed">
                  Understanding the impact of community investment is essential for:
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#E3B505] flex-shrink-0 mt-0.5" />
                    <span className="text-[#5C5852]">Demonstrating ROI to stakeholders</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#E3B505] flex-shrink-0 mt-0.5" />
                    <span className="text-[#5C5852]">Informing future programme design</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#E3B505] flex-shrink-0 mt-0.5" />
                    <span className="text-[#5C5852]">Building evidence for funding applications</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#E3B505] flex-shrink-0 mt-0.5" />
                    <span className="text-[#5C5852]">Celebrating community achievements</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Report Types */}
      <section 
        data-testid="report-types-section"
        className="py-20 md:py-28 bg-[#FAF9F6]"
      >
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-16">
            <span className="text-sm font-medium tracking-wide uppercase text-[#264653] mb-4 block">
              Report Options
            </span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold text-[#2D2A26] font-['Manrope']">
              Choose Your Report Type
            </h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {reportTypes.map((report, index) => (
              <div 
                key={report.title}
                data-testid={`report-card-${index}`}
                className="bg-white rounded-3xl p-8 border-2 hover:shadow-lg transition-all duration-300 flex flex-col"
                style={{ borderColor: report.color }}
              >
                <div 
                  className="w-14 h-14 rounded-2xl flex items-center justify-center mb-6"
                  style={{ backgroundColor: `${report.color}20` }}
                >
                  {index === 0 && <FileText className="w-7 h-7" style={{ color: report.color }} />}
                  {index === 1 && <BarChart3 className="w-7 h-7" style={{ color: report.color }} />}
                  {index === 2 && <Award className="w-7 h-7" style={{ color: report.color }} />}
                </div>
                
                <h3 className="text-xl md:text-2xl font-semibold text-[#2D2A26] mb-3 font-['Manrope']">
                  {report.title}
                </h3>
                <p className="text-[#5C5852] leading-relaxed mb-6">{report.description}</p>
                
                <ul className="space-y-2 mb-6 flex-grow">
                  {report.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm text-[#5C5852]">
                      <CheckCircle className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: report.color }} />
                      {feature}
                    </li>
                  ))}
                </ul>
                
                <div 
                  className="px-4 py-2 rounded-full text-sm font-medium text-center"
                  style={{ backgroundColor: `${report.color}15`, color: report.color }}
                >
                  Ideal for: {report.ideal}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-28 bg-[#264653]">
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold text-white mb-6 font-['Manrope']">
            Ready to Measure Your Impact?
          </h2>
          <p className="text-lg md:text-xl text-white/80 mb-10 max-w-2xl mx-auto">
            Get in touch to discuss your reporting needs. We'll help you choose the right report 
            type and tailor the analysis to your specific requirements.
          </p>
          <Link to="/contact">
            <Button 
              data-testid="reports-cta-btn"
              className="bg-[#E3B505] text-[#2D2A26] hover:bg-[#E3B505]/90 rounded-full h-14 px-10 text-base font-medium shadow-lg"
            >
              Enquire About Reports
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </main>
  );
};

export default ReportsPage;
